// rest-timer validation constants and helpers; see
// design-docs/rest-timer.md for context

import type { DeclaredState, ExerciseStat, FitnessState } from './user-state';
import type { Exercise } from './exercise';

export const REST_TIMER_MIN_SECONDS = 15;
export const REST_TIMER_MAX_SECONDS = 585;
export const REST_TIMER_STEP_SECONDS = 15;
export const REST_TIMER_DEFAULT_SECONDS = 120;

// whether the rest timer starts by itself when a set is logged, or only
// when the user taps Start Rest Timer (#6014)
export enum RestTimerStart {
  Auto = 'auto',
  Manual = 'manual',
}

// decide whether the rest timer starts by itself, for a stored declared state.
// older app versions read and write only restTimerEnabled, so while they are
// in use that field holds the user's latest choice: a stored restTimerStart
// that agrees with it adds nothing, one that disagrees was overtaken by an
// older app, and a missing or unknown one (old, damaged or newer document)
// has nothing to say. every case therefore reads restTimerEnabled; the enum
// becomes the source once those versions are retired (design-docs/rest-timer.md).
export function resolveRestTimerStart(
  declaredState: Pick<DeclaredState, 'restTimerEnabled' | 'restTimerStart'> | null | undefined,
): RestTimerStart {
  return declaredState?.restTimerEnabled === true ? RestTimerStart.Auto : RestTimerStart.Manual;
}

// record the user's start-mode choice. writes restTimerEnabled alongside so
// older app versions keep the matching behaviour: auto-start for Auto, no
// timer (their "off") for Manual.
export function applyRestTimerStart(
  declaredState: Pick<DeclaredState, 'restTimerEnabled' | 'restTimerStart'>,
  choice: RestTimerStart,
): void {
  declaredState.restTimerStart = choice;
  declaredState.restTimerEnabled = choice === RestTimerStart.Auto;
}

// snap to the nearest 15s step, then clamp to [15, 585]; non-finite
// input falls back to the module default
export function clampRestSeconds(n: number): number {
  // if input is NaN/Infinity, fall back to the default
  if (!Number.isFinite(n)) {
    return REST_TIMER_DEFAULT_SECONDS;
  }
  const snapped = Math.round(n / REST_TIMER_STEP_SECONDS) * REST_TIMER_STEP_SECONDS;
  // if below min, clamp up
  if (snapped < REST_TIMER_MIN_SECONDS) return REST_TIMER_MIN_SECONDS;
  // if above max, clamp down
  if (snapped > REST_TIMER_MAX_SECONDS) return REST_TIMER_MAX_SECONDS;
  return snapped;
}

// build the list of all valid duration options for a rest-timer picker:
// every 15s step from MIN to MAX, each labelled as M:SS. used by both the
// global default picker (program settings) and the per-exercise override
// picker (exercise stats).
export function getRestTimerOptions(): { seconds: number; label: string }[] {
  const opts: { seconds: number; label: string }[] = [];
  // for each step from min to max, include the formatted label
  for (let s = REST_TIMER_MIN_SECONDS; s <= REST_TIMER_MAX_SECONDS; s += REST_TIMER_STEP_SECONDS) {
    opts.push({ seconds: s, label: formatRestDuration(s) });
  }
  return opts;
}

// format seconds as M:SS with zero-padded seconds; negatives render as 0:00
export function formatRestDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

// resolve the rest-timer duration for an exercise: always a clamped positive
// integer seconds value. whether the timer starts by itself is a separate
// question, answered by resolveRestTimerStart.
// precedence: per-exercise override → global default → module default.
export function getRestSecondsForExercise(
  exercise: Pick<Exercise, '_id'>,
  declaredState: DeclaredState,
  fitnessState: Pick<FitnessState, 'exerciseStats'>,
): number {
  // if per-exercise override is set and positive, use it (clamped on read
  // so stale or off-grid persisted values still resolve to a valid step)
  const stat: ExerciseStat | undefined = fitnessState.exerciseStats?.[exercise._id];
  const override = stat?.restSeconds;
  if (typeof override === 'number' && override > 0) {
    return clampRestSeconds(override);
  }
  // fall back to the global default, clamped; if missing or non-positive,
  // use the module default
  const fallback = declaredState.defaultRestSeconds;
  if (typeof fallback === 'number' && fallback > 0) {
    return clampRestSeconds(fallback);
  }
  return REST_TIMER_DEFAULT_SECONDS;
}
